// Shared types + display helpers for the New contract rollout module.
import type { ChipTone } from '../components/interviews/Chip'

export type RolloutRow = {
  driver_id: string
  driver_name: string | null
  primary_email: string | null
  driver_status: string
  application_status: string | null
  excluded_dismissed: boolean
  in_scope: boolean
  service_since: string | null
  commencement_text: string | null
  envelope_id: string | null
  contract_status: 'none' | 'sent' | 'signed' | 'voided' | 'declined' | 'delivered'
  sent_at: string | null
  signed_at: string | null
  new_contract_signed: boolean
  blocked_no_email: boolean
  allocation_eligible_today: boolean
  allocation_block_reason: string | null
  contract_blocked: boolean
  contract_block_status: string | null
  contract_block_source: string | null
  contract_block_severity: 'temporary' | 'permanent' | null
  contract_blocked_since: string | null
  contract_block_reason: string | null
  // 3703: which agreement the contract envelope carried, and whether this
  // driver's route to the current terms is Addendum No. 1 rather than v6.
  contract_agreement_version?: string | null
  offered_addendum?: boolean
}

export type RolloutSummary = {
  would_send: number
  awaiting_signature: number
  signed: number
  blocked_no_email: number
  excluded_dismissed: number
}

// The five buckets a driver can fall into for filtering. excluded_dismissed and
// blocked_no_email take precedence over the envelope status, matching the chip.
export type StatusCategory =
  | 'excluded'
  | 'no_email'
  | 'signed'
  | 'awaiting'
  | 'not_sent'

export function statusCategory(row: RolloutRow): StatusCategory {
  if (row.contract_blocked) return 'excluded'
  if (row.blocked_no_email) return 'no_email'
  if (row.new_contract_signed) return 'signed'
  if (row.contract_status === 'sent') return 'awaiting'
  return 'not_sent'
}

export type StatusFilter = 'all' | 'not_sent' | 'awaiting' | 'signed' | 'excluded'

export const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'not_sent', label: 'Not sent' },
  { value: 'awaiting', label: 'Awaiting signature' },
  { value: 'signed', label: 'Signed' },
  { value: 'excluded', label: 'Blocked' },
]

export function matchesFilter(row: RolloutRow, filter: StatusFilter): boolean {
  if (filter === 'all') return true
  return statusCategory(row) === filter
}

// Chip appearance for a row. The Chip primitive supports green/red/amber/neutral
// tones; "Awaiting signature" needs blue, so it overrides via className while
// still using the same Chip component (matching the Chip tone class pattern).
export type ChipInfo = { label: string; tone: ChipTone; className?: string }

const BLUE_CHIP =
  'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-800'

export function chipInfo(row: RolloutRow): ChipInfo {
  switch (statusCategory(row)) {
    case 'excluded':
      return {
        label: row.contract_block_status
          ? `Blocked: ${row.contract_block_status}`
          : 'Blocked',
        tone: 'red',
      }
    case 'no_email':
      return { label: 'No email on file', tone: 'amber' }
    case 'signed':
      return { label: 'Signed', tone: 'green' }
    case 'awaiting':
      return { label: 'Awaiting signature', tone: 'neutral', className: BLUE_CHIP }
    case 'not_sent':
    default:
      return { label: 'Not sent', tone: 'neutral' }
  }
}

// The reason a driver cannot be sent a contract, for a disabled action button's
// title attribute.
export function blockReason(row: RolloutRow): string {
  if (row.contract_block_reason) return row.contract_block_reason
  if (row.allocation_block_reason) return row.allocation_block_reason
  if (row.contract_blocked) return 'Blocked from contract issue'
  if (row.blocked_no_email) return 'No email on file for this driver'
  return 'Not eligible'
}
